import { CSSInterpolation } from '@emotion/css/create-instance'
import { cx } from '@emotion/css'
import React, { type ComponentProps, type Ref } from 'react'

import type { ReactAnchorProps, ReactButtonProps } from '../../types'
import type { SemanticColorToken } from '../themes'
import { CssVars, NeutralColorToken, type ThemeColorToken } from '../themes'
import { BaseButton } from './BaseButton'
import {
  FoundationIcon,
  getIsSize16Icon,
  getIsSize24Icon,
} from './FoundationIcon'
import type { Icon16, Icon24 } from './FoundationIcon/icons'
import { HoverState } from './HoverState'
import { InternalButtonContent } from './internal/InternalButtonContent'
import { TextColorOverride } from './TextColorOverride'

type OmittedElementPropKeys =
  | 'children'
  | 'ref'
  | 'disabled'
  | 'className'
  | 'style'
  | 'css'
  | 'type'
  | 'aria-label'

type TypeIntersection =
  | ({ type: 'anchor'; innerRef?: Ref<HTMLAnchorElement> } & Omit<
      ReactAnchorProps,
      OmittedElementPropKeys
    >)
  | ({ type: 'button'; innerRef?: Ref<HTMLButtonElement> } & Omit<
      ReactButtonProps,
      OmittedElementPropKeys
    >)

/**
 * NOTE(lynn): Making aria-label required is meant to nudge a dev to provide an accessible name
 * (https://developer.paciellogroup.com/blog/2017/04/what-is-an-accessible-name/).
 */
export type Props = {
  /**
   * Defines an accessible name for `IconButton`. This gives screen readers a way to label a button
   * that would be otherwise just called `"button"` or `"link, (url)"`. Icons are not read out to screen
   * readers and an anchor tag that only reads out its URL may need more context.
   *
   * E.g. if this value is set to `"confirm trade"`, then a screen reader will read out, `"confirm trade,
   * button"` to the user.
   */
  'aria-label': string
  state?: false | 'active' | 'disabled'
  icon: Icon16 | Icon24
  title?: string
  backgroundColor?: SemanticColorToken | ThemeColorToken
  foregroundColor?: SemanticColorToken | ThemeColorToken
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
} & TypeIntersection

export const IconButton = ({
  icon,
  innerRef,
  state,
  type,
  overrideRootStyles,
  backgroundColor = NeutralColorToken.Bg1,
  foregroundColor = NeutralColorToken.Fg1,
  ...props
}: Props) => {
  const isDisabled = state === 'disabled'
  return (
    <HoverState color={backgroundColor} isActive={state === 'active'}>
      {({ colorVariable, hoverableClassName, rootClassName }) => (
        <TextColorOverride
          color={isDisabled ? NeutralColorToken.Fg3 : foregroundColor}
        >
          {({ rootClassName: textColorClassName }) => {
            const iconElement = (
              <InternalButtonContent state={state}>
                <span
                  // TODO(lynn): This wrapping span is required until we can actually pass
                  // attributes through to the svg element
                  aria-hidden
                  css={{ display: 'inherit' }}
                >
                  <FoundationIcon type={icon} />
                </span>
              </InternalButtonContent>
            )

            const baseButtonProps = {
              className: cx(
                rootClassName,
                !isDisabled && hoverableClassName,
                textColorClassName
              ),
              overrideRootStyles: [
                {
                  backgroundColor: colorVariable,
                  borderRadius: CssVars.size.borderRadius,
                  flexShrink: 0,
                  height: getIconButtonSize(icon),
                  width: getIconButtonSize(icon),
                },
                overrideRootStyles,
              ],
              // BaseButton doesn't support an 'active' state, but we want the same behavior
              // as 'default' for it.
              state: (state === 'active' ? 'default' : state) as ComponentProps<
                typeof BaseButton
              >['state'],
            }

            if (type === 'anchor') {
              return (
                <BaseButton
                  innerRef={innerRef as Ref<HTMLAnchorElement>}
                  type="anchor"
                  {...baseButtonProps}
                  {...(props as Omit<ReactAnchorProps, OmittedElementPropKeys>)}
                >
                  {iconElement}
                </BaseButton>
              )
            }

            return (
              <BaseButton
                innerRef={innerRef as Ref<HTMLButtonElement>}
                type="button"
                {...baseButtonProps}
                {...(props as Omit<ReactButtonProps, OmittedElementPropKeys>)}
              >
                {iconElement}
              </BaseButton>
            )
          }}
        </TextColorOverride>
      )}
    </HoverState>
  )
}

export function getIconButtonSize(icon: Props['icon']): number {
  if (getIsSize16Icon(icon)) {
    return 24
  }

  if (getIsSize24Icon(icon)) {
    return 40
  }

  return 0
}
